import useAgency from 'features/Agency/useAgency';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import ButtonFilterDropdown from 'general/components/quybka/Buttons/ButtonFilterDropdown';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import useWindowSize from 'hooks/useWindowSize';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ChargeStationListFilterView from './components/ChargeStationListFilterView';
import AppConstants from 'general/constants/AppConstants';
import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import useAppConfig from 'hooks/useAppConfig';
import useCharge from 'features/Charge/useCharge';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';

ChargeStationListView.propTypes = {};

const defaultColumnIds = [
  'CODE',
  'NAME',
  'OPERATOR',
  'MANAGEMENT_OBJECT',
  'AGENCY',
  'VEHICLE_TYPE',
  'AVAILABLE_POINT_COUNT',
  'POINT_TYPE',
  'STATUS',
  'TOTAL_TRANSACTION_COUNT',
  'TOTAL_ENERGY_SELL',
  'ADDRESS',
  'CREATED_AT',
];

function ChargeStationListView({ }) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const isInvestor = user?.role === 'AGENCY' && user?.agency?.primaryRole === 'INVESTOR';
  const isLandlord = user?.role === 'AGENCY' && user?.agency?.primaryRole === 'LANDLORD';
  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const [loading, setLoading] = useState(false);
  const [stationList, setStationList] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState(ChargeHelper.Filter.chargeStationList);
  const areaTree = useSelector((state) => state.app.areaTree);
  const appConfig = useAppConfig();
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'CODE',
        name: UIHelper.buildTableColumn('Mã trạm sạc'),
        selector: (row) => row?.stationCode,
        width: '150px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'NAME',
        name: UIHelper.buildTableColumn('Tên trạm sạc'),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.name}>
            <span>{row?.name}</span>
          </KTTooltip>
        ),
        width: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'OPERATOR',
        name: UIHelper.buildTableColumn('Nhà điều hành'),
        selector: (row) => row?.chargeOperator?.name,
        width: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'MANAGEMENT_OBJECT',
        name: UIHelper.buildTableColumn('Đối tượng quản lý'),
        selector: (row) =>
          Utils.isValid(row?.agencyId)
            ? Utils.isValid(row?.landlordId)
              ? 'Dự án HTKD'
              : row?.agencyId === appConfig?.groupAgencyId
                ? 'Dự án HTKD'
                : row?.investor?.primaryRole === 'COINVESTOR'
                  ? 'NĐT liên kết'
                  : 'NĐT nhượng quyền'
            : 'Nhà điều hành',
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'AGENCY',
        name: UIHelper.buildTableColumn('Nhà đầu tư'),
        // omit: !isAdmin,
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => {
              if (Utils.isValid(row?.agencyId) && !Utils.isValid(row?.landlordId) && row?.agencyId !== appConfig?.groupAgencyId) {
                Utils.openInNewTab(`/investor/${row?.agencyId}`);
              } else if (Utils.isValid(row?.landlordId)) {
                Utils.openInNewTab(`/landlord/${row?.landlordId}`);
              }
            }}
          >
            <KTTooltip offset={[0, 0]}
              text={
                Utils.isValid(row?.agencyId) && !Utils.isValid(row?.landlordId) && row?.agencyId !== appConfig?.groupAgencyId
                  ? row?.investor?.name
                  : row?.landlord?.name
              }>
              <span>
                {Utils.isValid(row?.agencyId) && !Utils.isValid(row?.landlordId) && row?.agencyId !== appConfig?.groupAgencyId
                  ? row?.investor?.name
                  : row?.landlord?.name
                }
              </span>
            </KTTooltip>
          </span>
        ),
        width: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'VEHICLE_TYPE',
        name: UIHelper.buildTableColumn('Loại phương tiện'),
        selector: (row) =>
          row?.vehicleTypes
            ?.map(type =>
              ChargeHelper.Data.vehicleTypeOptions.find(item => item.value === type)?.name
            )
            ?.filter(Boolean)
            ?.join(', ') ?? '',
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'AVAILABLE_POINT_COUNT',
        name: UIHelper.buildTableColumn('Trụ sạc còn trống'),
        selector: (row) =>
          `${ChargeHelper.Utils.getChargeStationAvailablePoints(row?.chargePoints)}/${row?.chargePoints?.length}`,
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'POINT_TYPE',
        name: UIHelper.buildTableColumn('Loại trụ sạc'),
        selector: (row) => ChargeHelper.Utils.getChargeStationPointTypes(row),
        width: '100px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        name: UIHelper.buildTableColumn('Trạng thái trạm sạc'),
        selector: (row) => (
          <KTLabel
            text={ChargeHelper.Utils.getChargeStationStatusName(row?.status)}
            isInline={true}
            size={KTLabelSize.medium}
            additionalClassName={`label-light-${ChargeHelper.Utils.getChargeStationStatusColor(
              row?.status
            )}`}
            additionalDescription={row?.reason}
          />
        ),
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'TOTAL_TRANSACTION_COUNT',
        name: UIHelper.buildTableColumn('Tổng số phiên sạc'),
        selector: (row) => Utils.formatNumber(row?.totalTransactions ?? 0),
        width: '140px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'TOTAL_ENERGY_SELL',
        name: UIHelper.buildTableColumn('Số điện bán được', 'kWh'),
        selector: (row) => Utils.isValid(row?.totalEnergy) ? Utils.formatNumber((_.toNumber(row?.totalEnergy) / 1000).toFixed(3)) : '',
        width: '140px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'ADDRESS',
        name: UIHelper.buildTableColumn('Địa chỉ'),
        selector: (row) => {
          const address = ChargeHelper.Utils.getChargeStationFullAddress(row);
          return (
            <KTTooltip offset={[0, 0]} text={address}>
              <span>{address}</span>
            </KTTooltip>
          );
        },
        minWidth: '260px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CREATED_AT',
        name: UIHelper.buildTableColumn('Ngày tạo'),
        selector: (row) => Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY'),
        width: '120px',
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        width: !isAdmin && !isInvestor && !isLandlord ? '100px' : '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        center: !isAdmin && !isInvestor && !isLandlord,
        cell: (row) => {
          const actions = buildMoreActions(row);
          if (_.isEmpty(actions)) return null;
          const button = UIHelper.buildMoreActionsButton(actions, true);
          if (actions.length === 1) {
            return <div className="d-flex justify-content-center w-100">{button}</div>;
          }
          return button;
        },
      },
    ];
  }, [areaTree, isAdmin, isInvestor, isLandlord]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
  useEffect(() => {
    let params = _.pickBy(
      filter,
      (value) => value !== null && value !== undefined && value !== ''
    );
    if (!params.status) {
      params.status = 'ALL';
    }
    ChargeHelper.API.requestGetChargeStationList(params).then((data) => {
      setStationList(data?.chargeStations);
      setTotal(data?.total);
    });
  }, [filter]);
  const { operatorList, operatorOptions } = useAgency();
  const windowSize = useWindowSize();

  const { managementObjectOptions } = useCharge();
  const [selectedManagementObject, setSelectedManagementObject] = useState('');
  const [selectedAgency, setSelectedAgency] = useState();

  // MARK: ---- METHODS ----
  const handleRefresh = () => {
    setFilter({ ...ChargeHelper.Filter.chargeStationList });
  };
  const buildMoreActions = (chargeStationDetail) => {
    const actions = [];
    // xem chi tiet
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargeStationView.code)) {
      actions.push({
        text: 'Xem chi tiết',
        iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          router.navigate(`${chargeStationDetail?.stationId}`);
        },
      });
    }
    if (chargeStationDetail?.status === ChargeConstants.ChargeStationStatus.DELETED) {
      return actions;
    }
    if (isAdmin || isInvestor || isLandlord) {
      // chinh sua
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargeStationUpdate.code)) {
        actions.push({
          text: 'Chỉnh sửa',
          iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            router.navigate(`${chargeStationDetail?.stationId}/edit`);
          },
        });
        if (
          [ChargeConstants.ChargeStationStatus.NORMAL].includes(chargeStationDetail?.status)
        )
          actions.push({
            text: 'Ngừng hoạt động',
            iconElement: <i className="fa-regular fa-stop" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.showDialogConfirmStopChargeStation(chargeStationDetail, () => {
                handleRefresh();
              });
            },
          });
        if (
          [
            ChargeConstants.ChargeStationStatus.STOPPED,
            ChargeConstants.ChargeStationStatus.DISCONNECTED,
          ].includes(chargeStationDetail?.status)
        )
          actions.push({
            text: 'Khôi phục hoạt động',
            iconElement: <i className="fa-regular fa-play" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.showDialogConfirmRestoreChargeStation(chargeStationDetail, () => {
                handleRefresh();
              });
            },
          });
        if (
          [ChargeConstants.ChargeStationStatus.NORMAL,
          ChargeConstants.ChargeStationStatus.STOPPED,
          ChargeConstants.ChargeStationStatus.DISCONNECTED,
          ].includes(chargeStationDetail?.status)
        )
          actions.push({
            text: 'Bảo trì',
            iconElement: <i className="fa-regular fa-user-helmet-safety" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.showDialogConfirmMaintainChargeStation(chargeStationDetail, () => {
                handleRefresh();
              });
            },
          });
        if (
          [ChargeConstants.ChargeStationStatus.MAINTAINING].includes(chargeStationDetail?.status)
        )
          actions.push({
            text: 'Hoàn thành bảo trì',
            iconElement: <i className="fa-regular fa-clipboard-check" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.showDialogConfirmCompleteMaintainChargeStation(chargeStationDetail, () => {
                handleRefresh();
              });
            },
          });
        if (
          [ChargeConstants.ChargeStationStatus.NORMAL,
          ChargeConstants.ChargeStationStatus.MAINTAINING,
          ChargeConstants.ChargeStationStatus.STOPPED,
          ChargeConstants.ChargeStationStatus.DISCONNECTED,
          ].includes(chargeStationDetail?.status)
        )
          actions.push({
            text: 'Ẩn trạm sạc',
            iconElement: <i className="fa-regular fa-store-lock" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.showDialogConfirmHideChargeStation(chargeStationDetail, () => {
                handleRefresh();
              });
            },
          });
        if (
          [ChargeConstants.ChargeStationStatus.HIDDEN].includes(chargeStationDetail?.status)
        )
          actions.push({
            text: 'Bỏ ẩn trạm sạc',
            iconElement: <i className="fa-regular fa-store" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.showDialogConfirmUnhideChargeStation(chargeStationDetail, () => {
                handleRefresh();
              });
            },
          });
      }
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargeStationDelete.code))
        actions.push({
          text: 'Xoá trạm sạc',
          iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmDeleteChargeStation(chargeStationDetail, () => {
              handleRefresh();
            });
          },
        });
    }
    return actions;
  };

  const dropdownAgencyEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="agencyId_filter"
        name="agencyId_filter"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between 
              ${ChargeHelper.Filter.chargeStationList?.managedBy === '' ? 'disabled' : ''
              }`}
            type="button"
          >
            {selectedAgency
              ? selectedAgency?.name
              : ChargeHelper.Filter.chargeStationList?.managedBy === AgencyConstants.AgencyRole.INVESTOR
                ? 'Chọn nhà đầu tư'
                : 'Chọn chủ mặt bằng'}
          </button>
        }
        selectedValue={selectedAgency}
        subTitleType="email"
        dropdownMenuItems={[{ name: 'AGENCY', role: 'AGENCY' }]}
        getSubItems={async (item) => {
          return AgencyHelper.API.requestGetAgencyList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
            role:
              ChargeHelper.Filter.chargeStationList?.managedBy === AgencyConstants.AgencyRole.INVESTOR
                ? 'INVESTOR'
                : 'LANDLORD',
          }).then((data) => {
            return {
              values: data.agencies?.map((row) => ({
                id: row?.agencyId,
                name: row?.name,
                item: row,
                email: row?.email,
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedAgency(value);
          if (ChargeHelper.Filter.chargeStationList?.managedBy === AgencyConstants.AgencyRole.INVESTOR) {
            ChargeHelper.Filter.chargeStationList = {
              ...ChargeHelper.Filter.chargeStationList,
              agencyId: value?.id,
              page: 0,
            };
            handleRefresh();
          } else {
            ChargeHelper.Filter.chargeStationList = {
              ...ChargeHelper.Filter.chargeStationList,
              landlordId: value?.id,
              page: 0,
            };
            handleRefresh();
          }
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        disabled={ChargeHelper.Filter.chargeStationList?.managedBy === ''}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'AGENCY',
          role: 'AGENCY',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectedAgency(null);
              ChargeHelper.Filter.chargeStationList = {
                ...ChargeHelper.Filter.chargeStationList,
                agencyId: '',
                landlordId: '',
                page: 0,
              };
              handleRefresh();
            })}
          </div>
        }
      />
    );
  }, [selectedAgency, ChargeHelper.Filter.chargeStationList?.managedBy]);

  return (
    <div>
      {UIHelper.buildCard(
        <div className="pb-4">
          {/* header */}
          <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                Danh sách trạm sạc
              </span>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                Tổng:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {Utils.formatNumber(total)} trạm sạc
                </span>
              </span>
            </div>
            <div>
              {isAdmin && Utils.checkPermission(permissionList, AppConstants.permissionCode.chargeStationCreate.code) &&
                UIHelper.buildGreenButton(
                  'Thêm trạm sạc',
                  () => {
                    router.navigate('add');
                  },
                  <i className="fa-regular fa-circle-plus" />
                )}
            </div>
          </div>

          {/* table */}
          <div className="">
            {/* filter */}
            <div className="row m-0 p-0 px-2">
              {/* tim kiem */}
              <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel('Tìm kiếm')}
                <KTFormSearch
                  name="search"
                  placeholder="Theo mã trạm sạc, tên trạm sạc, địa chỉ..."
                  value={filter?.q}
                  onSubmit={(text) => {
                    ChargeHelper.Filter.chargeStationList = {
                      ...ChargeHelper.Filter.chargeStationList,
                      q: text,
                      page: 0,
                    };
                    handleRefresh();
                  }}
                />
              </div>
              {/* loc theo nha dieu hanh */}
              <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel('Nhà điều hành')}
                <KTBSDropdown
                  name="operator"
                  toggleElement={
                    <button
                      className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                      type="button"
                    >
                      {_.find(
                        operatorList,
                        (item) =>
                          item?.operatorId === ChargeHelper.Filter.chargeStationList?.operatorId
                      )?.name ?? 'Tất cả'}
                    </button>
                  }
                  selectedValue={ChargeHelper.Filter.chargeStationList?.operatorId}
                  dropdownMenuItems={[{ name: 'Tất cả', value: '' }, ...operatorOptions]}
                  dropdownMenuClassName="overflow-auto w-100"
                  dropdownMenuStyle={{ maxHeight: '30vh' }}
                  onChange={(value) => {
                    ChargeHelper.Filter.chargeStationList = {
                      ...ChargeHelper.Filter.chargeStationList,
                      operatorId: value,
                      page: 0,
                    };
                    handleRefresh();
                  }}
                  isDropdownForm={true}
                />
              </div>
              {/* doi tuong quan ly */}
              <div className="col mb-4 p-0 px-2">
                {UIHelper.buildFormLabel('Đối tượng quản lý')}
                <KTBSDropdown
                  name="managementObject_filter"
                  toggleElement={
                    <button
                      className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
                      type="button"
                    >
                      {_.find(managementObjectOptions,
                        (item) =>
                          item.value === ChargeHelper.Filter.chargeStationList?.managedBy
                      )?.name ?? 'Tất cả'}
                    </button>
                  }
                  selectedValue={ChargeHelper.Filter.chargeStationList?.managedBy}
                  dropdownMenuItems={[{ name: 'Tất cả', value: '' }, ...managementObjectOptions]}
                  dropdownMenuClassName="overflow-auto w-100"
                  dropdownMenuStyle={{ maxHeight: '30vh' }}
                  onChange={(value) => {
                    setSelectedAgency(null);
                    ChargeHelper.Filter.chargeStationList = {
                      ...ChargeHelper.Filter.chargeStationList,
                      managedBy: value,
                      agencyId: '',
                      landlordId: '',
                      page: 0,
                    };
                    handleRefresh();
                  }}
                  isDropdownForm={true}
                />
              </div>
              {/* loc theo loai phuong tien */}
              <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel(
                  ChargeHelper.Filter.chargeStationList?.managedBy === AgencyConstants.AgencyRole.INVESTOR
                    ? 'Nhà đầu tư NQ'
                    : (ChargeHelper.Filter.chargeStationList?.managedBy === AgencyConstants.AgencyRole.LANDLORD || ChargeHelper.Filter.chargeStationList?.managedBy === 'ADMIN')
                      ? 'Chủ mặt bằng'
                      : 'Đối tác'
                )}
                {dropdownAgencyEl}
              </div>
              {/* thiet lap cot */}
              <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                {/* bo loc */}
                <ButtonFilterDropdown
                  filtering={
                    ChargeHelper.Filter.chargeStationList.status !== '' ||
                    ChargeHelper.Filter.chargeStationList.q !== '' ||
                    ChargeHelper.Filter.chargeStationList.agencyId !== '' ||
                    ChargeHelper.Filter.chargeStationList.landlordId !== '' ||
                    ChargeHelper.Filter.chargeStationList.areaId !== '' ||
                    ChargeHelper.Filter.chargeStationList.managedBy !== '' ||
                    ChargeHelper.Filter.chargeStationList.vehicleType !== '' ||
                    ChargeHelper.Filter.chargeStationList.type !== '' ||
                    ChargeHelper.Filter.chargeStationList.isTestStation !== 0
                  }
                  dropdownContent={
                    <ChargeStationListFilterView
                      isAdmin={isAdmin}
                      onCancel={() => {
                        setSelectedAgency('');
                        handleRefresh();
                      }}
                      onApply={() => {
                        handleRefresh();
                      }}
                    />
                  }
                />
                <DropdownTableConfigColumn
                  columnIds={columnIds}
                  fullColumns={fullColumns}
                  fullColumnIds={fullColumnsIdSorted}
                  onChangeCheckboxValue={onChangeCheckboxValue}
                  resetDefaultTableColumn={resetDefaultTableColumn}
                  onChangeFullColumnIds={(ids) => {
                    setFullColumnIds(ids);
                    const key = prefKey + '_FULL_COLUMN_IDS';
                    localStorage.setItem(key, ids.join('|'));
                  }}
                  onSelectAll={onSelectAll}
                  onUnSelectAll={onUnSelectAll}
                  saveChanges={saveChanges}
                />
              </div>
            </div>

            <div className="px-4">
              <KTTable
                columns={columns}
                items={stationList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                )}
              />
            </div>
          </div>

          {/* pagination */}
          <div className="px-4 mt-2">
            <KTPagination
              currentPage={filter.page + 1}
              rowCount={total}
              rowsPerPage={filter.limit}
              currentItemInPageCount={stationList?.length}
              onChangePage={(newPage) => {
                const iNewPage = parseInt(newPage);
                ChargeHelper.Filter.chargeStationList = {
                  ...ChargeHelper.Filter.chargeStationList,
                  page: iNewPage - 1,
                };
                handleRefresh();
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                const iNewRowsPerPage = parseInt(newRowsPerPage);
                ChargeHelper.Filter.chargeStationList = {
                  ...ChargeHelper.Filter.chargeStationList,
                  limit: iNewRowsPerPage,
                  page: 0,
                };
                handleRefresh();
              }}
              loading={false}
              color={KTPaginationColor.success}
              appendText="trạm sạc"
            />
          </div>
        </div>,
        false
      )}
    </div>
  );
}

export default ChargeStationListView;
